import axios from "axios";
import db from "services/firebase";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";

interface ClickUpTask {
  id: string;
  name: string;
  status: { 
    status: string;
    id: string;
    color: string;
    type: string;
    orderindex: number;
  };
  list: { 
    id: string;
    name: string;
    access: boolean;
  };
  date_created: string;
  date_updated: string;
  archived: boolean;
  assignees: Array<{
    id: string;
    username: string;
    color: string;
    email: string;
    profilePicture: string | null;
  }>;
  creator: {
    id: number;
    username: string;
    color: string;
    email: string;
    profilePicture: string | null;
  };
  due_date: string | null;
  folder: {
    id: string;
    name: string;
    hidden: boolean;
    access: boolean;
  };
  priority: {
    color: string;
    id: string;
    orderindex: string;
    priority: string;
  } | null;
  space: {
    id: string;
  };
  team_id: string;
  url: string;
  description: string | null;
  custom_fields: Array<any>;
  watchers: Array<any>;
}

interface TaskData {
  count: number;
  tasks: Array<{
    task_id: string;
    task_name: string;
    status: string;
    list_id: string;
    created: string;
    updated: string;
    assignee_id: string;
    checked: boolean;
    date: string;
    deliverable: Record<string, any>;
    email: boolean;
    duedate: string;
    owner: string;
    priority: string;
    tenantId: string;
    username: string;
  }>;
  last_checked: string;
  status: "active" | "access_issue" | "no_tasks";
  tenant_id: string;
}

interface ClickUpResponse {
  success: boolean;
  tasks?: ClickUpTask[];
  error?: string;
  taskIssues?: string[];
}

interface ClickUpWorkspace {
  team_id: string;
  name: string;
  url: string;
}

export const createClickUpService = (accessToken: string) => {
  const apiBaseUrl = "https://api.clickup.com/api/v2";

  const axiosInstance = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      Authorization: `Bearer ${accessToken}`, 
      Accept: 'application/json',
    },
  });

  const checkClickUpTasks = async (teamId: string): Promise<ClickUpResponse> => {
    try {
      const options = {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };
      const response = await axiosInstance.get(`/team/${teamId}/task`, options);

      return {
        success: true,
        tasks: response.data.tasks,
      };
    }catch (error: any) {
      if (error.response?.status === 403 || error.response?.data?.err?.includes("Unauthorized")) {
        return {
          success: false,
          error: "Insufficient permissions for workspace",
          taskIssues: [teamId],
        };
      }
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  };

  const createClickUpTask = async (listId: string, name: string, description: string, priority: string, dueDate: string, clickUpUserId?: string): Promise<ClickUpResponse> => {
    try {
      if (!accessToken) {
        return {
          success: false,
          error: "Access token is missing"
        };
      }
      const response = await axios.post('https://us-central1-slate-134a0.cloudfunctions.net/createClickUpTask', {
        listId,
        name,
        description,
        priority,
        status: "to do",
        assignees: clickUpUserId ? [parseInt(clickUpUserId, 10)] : [],
        start_date: new Date().getTime(),
        due_date: new Date(dueDate).getTime(),
        due_date_time: true,
        access_token: accessToken 
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        tasks: [response.data]
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to create task"
      };
    }
  };

  const mapClickUpStatus = (status: { status: string }): string => {
    const statusLower = status.status.toLowerCase();
    switch (statusLower) {
      case "complete":
      case "completed":
      case "done":
        return "Completed";
      case "to do":
      case "todo":
      case "in progress":
      default:
        return "Incomplete";
    }
  };


  const mapClickUpPriority = (priority: { priority: string } | null): string => {
    if (!priority) return "Low";
    
    switch (priority.priority.toLowerCase()) {
      case "urgent":
        return "Urgent";
      case "high":
        return "High";
      case "normal":
        return "Normal";
      case "low":
        return "Low";
      case "clear":
      default:
        return "Clear";
    }
  };

  // const saveClickUpTasks = async (
  //   tenantId: string,
  //   teamId: string,
  //   tasks: ClickUpTask[] | null,
  //   status: "active" | "access_issue" | "no_tasks",
  // ) => {
  //   try {
  //     const taskData: TaskData = {
  //       count: tasks ? tasks.length : 0,
  //       tasks: tasks
  //         ? tasks.map((task) => ({
  //             task_id: task.id,
  //             task_name: task.name,
  //             status: mapClickUpStatus(task.status),
  //             list_id: task.list.id,
  //             created: task.date_created,
  //             updated: task.date_updated,
  //             assignee_id: task.assignees[0]?.id || "",
  //             checked: false,
  //             date: task.due_date || "",
  //             deliverable: task.custom_fields || {},
  //             email: Boolean(task.creator?.email),
  //             duedate: task.due_date || "",
  //             owner: task.creator?.username || "",
  //             priority: mapClickUpPriority(task.priority),
  //             tenantId: tenantId,
  //             username: task.creator?.username || ""
  //           }))
  //         : [],
  //       last_checked: new Date().toISOString(),
  //       status,
  //       tenant_id: tenantId,
  //     };

  //     const tenantRef = doc(db, "tenants", tenantId);
  //     const tasksRef = collection(tenantRef, "tasklists");
  //     const batch = writeBatch(db);

  //     if (status === "active" && tasks) {
  //       for (const task of taskData.tasks) {
  //         const existingTaskQuery = query(
  //           tasksRef,
  //           where("task_id", "==", task.task_id),
  //           where("list_id", "==", task.list_id)
  //         );
  //         const existingTaskSnapshot = await getDocs(existingTaskQuery);

  //         if (existingTaskSnapshot.empty) {
  //           batch.set(doc(tasksRef), {
  //             ...task,
  //             source: "ClickUp",
  //             created_at: new Date().toISOString(),
  //             scan_date: taskData.last_checked,
  //           });
  //         } else {
  //           const existingDoc = existingTaskSnapshot.docs[0];
  //           batch.update(doc(tasksRef, existingDoc.id), {
  //             scan_date: taskData.last_checked,
  //             status: task.status,
  //             updated: task.updated,
  //           });
  //         }
  //       }
  //     } else if (status === "access_issue") {
  //       // No tasks saved for workspaces with access issues
  //     } else if (status === "no_tasks") {
  //       const existingTaskQuery = query(
  //         tasksRef,
  //         where("list_id", "==", teamId),
  //         where("status", "==", "access_issue")
  //       );
  //       const existingTaskSnapshot = await getDocs(existingTaskQuery);
  //       existingTaskSnapshot.forEach((docSnap) => {
  //         batch.delete(doc(tasksRef, docSnap.id));
  //       });
  //     }

  //     const tenantRef2 = doc(db, "tenants", tenantId);
  //     const clickupRef = collection(tenantRef2, "integrations");
  //     const q = query(clickupRef, where("name", "==", "ClickUp"));
  //     const querySnapshot = await getDocs(q);
      
  //     if (!querySnapshot.empty) {
  //       const integrationDoc = querySnapshot.docs[0];
  //       const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationDoc.id);
  //       batch.update(integrationRef, {
  //         last_scan: new Date().toISOString(),
  //       });
  //     }

  //     await batch.commit();
  //     return { success: true };
  //   } catch (error: any) {
  //     console.error("Error saving ClickUp tasks:", error);
  //     return {
  //       success: false,
  //       error: error.message || "Unknown error",
  //     };
  //   }
  // };

  // const scanWorkspacesForTasks = async (
  //   workspaces: Array<{
  //     team_id: string;
  //     name: string;
  //     url: string;
  //   }>,
  //   tenantId: string,
  //   integrationId: string
  // ) => {
  //   try {
  //     let taskCount = 0;
  //     const taskIssues: string[] = [];
  //     const batch = writeBatch(db);

  //     for (const workspace of workspaces) {
  //       if (!workspace.team_id) {
  //         console.error(`Skipping workspace with undefined team_id:`, workspace);
  //         continue;
  //       }
  //       const result = await checkClickUpTasks(workspace.team_id);
  //       console.log(result);

  //       if (result.success && result.tasks) {
  //         const status = result.tasks.length > 0 ? "active" : "no_tasks";
  //         taskCount += result.tasks.length;
  //         const saveResult = await saveClickUpTasks(
  //           tenantId,
  //           workspace.team_id,
  //           result.tasks,
  //           status,
  //         );
  //         if (!saveResult.success) {
  //           console.error(`Failed to save tasks for ${workspace.team_id}:`, saveResult.error);
  //         }
  //       } else if (result.taskIssues) {
  //         taskIssues.push(...result.taskIssues);
  //         const saveResult = await saveClickUpTasks(
  //           tenantId,
  //           workspace.team_id,
  //           null,
  //           "access_issue",
  //         );
  //         if (!saveResult.success) {
  //           console.error(`Failed to save access issue for ${workspace.team_id}:`, saveResult.error);
  //         }
  //       }
  //     }

  //     if (taskIssues.length > 0) {
  //       const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
  //       batch.update(integrationRef, {
  //         task_issues: taskIssues,
  //         last_scan: new Date().toISOString(),
  //       });
  //       await batch.commit();
  //     }

  //     if (taskIssues.length > 0) {
  //       showInfoToast({
  //         message: `Scan completed: ${taskCount} tasks found in ${
  //           workspaces.length - taskIssues.length
  //         } workspaces. Access issues detected for ${taskIssues.length} workspaces.`,
  //       });
  //       return {
  //         success: false,
  //         error: "Access issues detected for some workspaces",
  //         taskIssues,
  //         totalTasks: taskCount,
  //         scannedWorkspaces: workspaces.length,
  //       };
  //     }

  //     showSuccessToast({
  //       message: `Task scan completed. Found ${taskCount} tasks across ${workspaces.length} workspaces.`,
  //     });

  //     return {
  //       success: true,
  //       totalTasks: taskCount,
  //       scannedWorkspaces: workspaces.length,
  //     };
  //   } catch (error: any) {
  //     const errorMessage = error.message || "Unknown error";
  //     console.error("Error scanning tasks:", errorMessage);
  //     showErrorToast({ message: "Failed to scan tasks" });
  //     return {
  //       success: false,
  //       error: errorMessage,
  //       taskIssues: [],
  //       totalTasks: 0,
  //       scannedWorkspaces: workspaces.length,
  //     };
  //   }
  // };

  const checkUser2FAStatus = async (): Promise<ClickUpResponse> => {
    try {
      const response = await axiosInstance.get(`/user`);
      return {
        success: true,
        error: "2FA status not available via API. Check ClickUp settings manually.",
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to fetch user data",
      };
    }
  };

  const getClickUpLists = async (teamId: string): Promise<ClickUpResponse> => {
    try {
      const options = {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };
      const response = await axiosInstance.get(`/team/${teamId}/list`, options);
      return {
        success: true,
        tasks: response.data.lists
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to fetch lists"
      };
    }
  };

  return {
    checkClickUpTasks,
    createClickUpTask,
    checkUser2FAStatus,
    getClickUpLists,
  };
};

export const exchangeCodeForClickUpToken = async (code: string, currentUser: any, navigate: (path: string, options?: { replace: boolean }) => void) => {
  let toastId;
  try {
      toastId = showLoadingToast({ message: 'Authenticating with ClickUp...' });
      const response = await fetch(
        `https://us-central1-slate-134a0.cloudfunctions.net/clickUpOAuthCallback?code=${encodeURIComponent(code)}`,
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
          },
        }
      );
  
      if (!response.ok) {
        let errorMessage = 'Failed to exchange code for token';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `HTTP error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
  
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error("Invalid response format from server");
      }
      if (!data.access_token) {
        throw new Error("No access token received from server");
      }
  
      updateLoadingToast(toastId, {
        message: 'Token received, fetching workspaces...',
        type: 'info',
        isLoading: true,
      });
  
      localStorage.setItem('clickup_access_token', data.access_token);

      const integrationId = await fetchAndSaveClickUpTasks(
        data.access_token,
        data.workspaces,
        data.userId,
        data.lists,
        data.users,
        currentUser,
        navigate
      );
      dismissLoadingToast(toastId);
      showSuccessToast({message: 'ClickUp authentication successful'});
      
      navigate(`/integrations?id=${integrationId?.id}&service=ClickUp`, { replace: true });
    } catch (error: any) {
      showErrorToast({message: 'ClickUp authentication failed, please try again later'});
      dismissLoadingToast(toastId);
      localStorage.removeItem('clickup_access_token');
      navigate('/integrations', { replace: true });
      throw error;
    }
  };
  
  export const fetchAndSaveClickUpTasks = async (
    accessToken: string,
    workspaces: ClickUpWorkspace[],
    userId: string,
    lists: any[],
    users: any[],
    currentUser: any,
    navigate: (path: string, options?: { replace: boolean }) => void
  ) => {
    try {
      const members = users?.[workspaces[0].team_id] || []; 
      const integrationId = await saveClickUpIntegration(
        {
          accessToken,
          workspace: workspaces[0],
          userId,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          userName: currentUser?.userName,
          email: currentUser?.email,
        },
        lists,
        members,
        currentUser
      );
  
      return {
        success: true,
        scannedWorkspaces: workspaces.length,
        id:integrationId?.integrationId
      };
    } catch (error: any) {
      showErrorToast({ message: error.message || 'Error processing tasks' });
      throw error;
    }
  };
  
  const saveClickUpIntegration = async (data: any, listData: any, users: any[], currentUser: any) => {
    try {
      let integrationId: string | undefined;
      const { doc, collection, addDoc, getDoc, setDoc, updateDoc, serverTimestamp, writeBatch } = await import("firebase/firestore");
  
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
  
      const tenantRef = doc(db, "tenants", data.tenantId);
      const clickupRef = collection(tenantRef, "integrations");
      const vendorsRef = collection(tenantRef, "vendors");
      const accessReviewRef = doc(tenantRef, "access_review", "clickup");
  
      const newData = {
        accessToken: data.accessToken,
        name: "ClickUp",
        group: "Task Management and Productivity Platform",
        img_path: "/integrations/ClickUp.svg",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: data.tenantId,
        uid: data.uid,
        createdAt: serverTimestamp(),
        userId: data.userId,
        workspace_name: data.workspace.name,
        team_id: data.workspace.team_id,
        workspace_url: data.workspace.url,
        lists: listData && listData[data.workspace.team_id] ? {
          [data.workspace.team_id]: listData[data.workspace.team_id].map((list: any) => ({
            id: list.id,
            name: list.name,
            folder: {
              id: list.folder.id,
              name: list.folder.name,
            },
            space: {
              id: list.space.id,
              name: list.space.name,
            },
            task_count: list.task_count,
            permission_level: list.permission_level
          }))
        } : {}
      };
  
      const vendorData = {
        authentication_type: "OAuth",
        category: "Task Management and Productivity Platform",
        created_at: serverTimestamp(),
        due_deligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "clickup_logo.png",
            size: 3395,
            url: "/integrations/ClickUp.svg",
          },
        ],
        owner: currentUser?.userName || "",
        policy_url: "https://clickup.com/security",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://clickup.com/terms",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "ClickUp",
        website_url: "https://clickup.com"
      };
  
      const membersMap: { [key: string]: any } = {};
      for (const user of users || []) {
        if (user.id) {
          const roleName = user.role === 1 ? "Admin" : user.role === 2 ? "Member" : " ";
          membersMap[user.id] = {
            id: user.id,
            email: user.email,
            name: user.name || user.initials || "Unknown",
            accountRole: roleName,
            platform: "ClickUp",
            tenantId: currentUser.tenantId,
            uid: currentUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: user.last_active ? "active" : "invited",
          };
        }
      }
  
      const clickupDoc = await getDoc(accessReviewRef);
      if (clickupDoc.exists()) {
        await updateDoc(accessReviewRef, {
          members: {
            ...clickupDoc.data().members,
            ...membersMap,
          },
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(accessReviewRef, {
          platform: "ClickUp",
          members: membersMap,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
  
      const batch = writeBatch(db);
      const docRef = await addDoc(clickupRef, newData)
      integrationId = docRef.id;
      await addDoc(vendorsRef, vendorData);
      await batch.commit();
  
      return { success: true, integrationId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  };

export default createClickUpService;